/* eslint-disable no-console */
import { getEnvVar } from '@mc/client-env'
import { getAnonymousId } from '@mc/track-event'

type TrackedSprigUserAttributes = {
  isLoggedIn?: boolean
}

const SHOULD_LOG =
  getEnvVar('LOG_SURVEY_CALLS') === true ||
  getEnvVar('LOG_SURVEY_CALLS') === 'true'

const isSprigUnavailable = () =>
  typeof window === 'undefined' || typeof window.Sprig === 'undefined'

const isSprigAvailable = () => !isSprigUnavailable()

const sprigConfigure = async (): Promise<void> => {
  if (typeof window === 'undefined') return // Early return for when not in browser

  if (isSprigAvailable()) return // Early return if Sprig is already configured

  const { sprig } = await import('@sprig-technologies/sprig-browser')

  const sprigKey = getEnvVar('SPRIG_KEY') as string

  if (!sprigKey) {
    console.warn('SPRIG_KEY environment variable is not set')
    return
  }

  sprig.configure({ environmentId: sprigKey })

  // Wait for Sprig to be ready:
  await new Promise<void>((resolve) => {
    const checkInterval = setInterval(() => {
      if (isSprigAvailable()) {
        clearInterval(checkInterval)
        resolve()
      }
    }, 100)

    // Timeout after 5 seconds
    setTimeout(() => {
      clearInterval(checkInterval)
      if (isSprigUnavailable()) {
        console.warn('Sprig failed to initialize after 5 seconds')
      }
      resolve()
    }, 5000)
  })
}

/**
 * Safely executes a Sprig command after ensuring Sprig is configured.
 */
const executeSprigCommand = async <K extends keyof SprigAPIActions>(
  command: K,
  ...args: Parameters<SprigAPIActions[K]>
): Promise<void> => {
  await sprigConfigure()

  if (isSprigUnavailable()) {
    console.warn('Sprig not available')
    return
  }

  window.Sprig(command, ...args)

  if (SHOULD_LOG) {
    console.groupCollapsed(`Sprig: ${command}`)
    console.info('Arguments:', ...args)
    console.groupEnd()
  }
}

export const sprigIdentifyAndSetAttributes = async (
  attributes?: TrackedSprigUserAttributes,
): Promise<void> =>
  executeSprigCommand('identifyAndSetAttributes', {
    userId: getAnonymousId() ?? undefined,
    attributes,
  })

export const sprigTrackEvent = async (
  eventName: string,
  properties?: Record<string, unknown>,
): Promise<void> =>
  executeSprigCommand('identifyAndTrack', {
    eventName,
    userId: getAnonymousId() ?? undefined,
    properties,
  })

/**
 * Sets a user attribute in Sprig.
 */
export const sprigSetAttribute = async <
  K extends keyof TrackedSprigUserAttributes,
>(
  attribute: K,
  value: NonNullable<TrackedSprigUserAttributes[K]>,
): Promise<void> => executeSprigCommand('setAttribute', attribute, value)

export const sprigLogout = async (): Promise<void> =>
  executeSprigCommand('logoutUser')
